import type { MutableRefObject, ReactNode } from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocalStorage } from 'util/useLocalStorage';
import { useE2E } from 'hooks/useE2E';
import { useTourRegistry } from './tourRegistry';

export interface BaseTourStop {
  id: string;
  placement: 'top' | 'right-start' | 'left-start' | 'right-end' | 'left';
  text: string;
  title: string;
  image?: string;
  emphasize: string[];
  position?: {
    top?: string;
    right?: string;
    bottom?: string;
    left?: string;
  };
}

export interface TourContextInterface<
  TStop extends BaseTourStop,
  T extends HTMLElement,
> {
  hasCompletedTour: boolean;
  currentStop: TStop | undefined;
  ref: MutableRefObject<T | null>;
  isFirstStop: boolean;
  isLastStop: boolean;
  totalStops: number;
  currentStopNumber: number;
  goToNextStop: () => void;
  goToPreviousStop: () => void;
  startTour: () => void;
  skipTour: () => void;
}

export interface TourConfig<
  TStop extends BaseTourStop,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  TStopId extends string,
> {
  tour: TStop[];
  localStorageKey: string;
  contextName: string;
  showInGlobalHelp?: boolean;
  legacyCompletionStopIds?: TStopId[];
}

export function createTourContext<
  TStop extends BaseTourStop,
  TStopId extends string,
>() {
  type ContextType = TourContextInterface<TStop, HTMLElement>;

  const Context = createContext<ContextType | null>(null);

  const useTourStop = <T extends HTMLElement>(
    tourStopId: TStopId,
  ):
    | TourContextInterface<TStop, T>
    | { currentStop: TStop | undefined; ref: null } => {
    const context = useContext(Context);

    if (!context) {
      return {
        currentStop: undefined,
        ref: null,
      };
    }

    const { currentStop } = context;

    if (currentStop?.id !== tourStopId) {
      return {
        currentStop: currentStop,
        ref: null,
      };
    }

    return context as unknown as TourContextInterface<TStop, T>;
  };

  const useTour = (contextName: string) => {
    const context = useContext(Context);

    if (!context) {
      throw new Error(`${contextName} is not defined`);
    }

    return context;
  };

  const TourProvider = ({
    children,
    config,
  }: {
    children: ReactNode;
    config: TourConfig<TStop, TStopId>;
  }) => {
    const isE2E = useE2E();
    const registerTour = useTourRegistry()?.registerTour;
    const { tour, localStorageKey } = config;

    const [persistedTourState, setPersistedTourState] = useLocalStorage<
      boolean | TStopId[]
    >(localStorageKey, false);
    const [stopIndex, setStopIndex] = useState(0);
    const ref = useRef<HTMLElement>(null);

    const hasCompletedTour = Array.isArray(persistedTourState)
      ? tour.every((stop) => persistedTourState.includes(stop.id as TStopId)) ||
        (config.legacyCompletionStopIds?.every((stopId) =>
          persistedTourState.includes(stopId),
        ) ??
          false)
      : persistedTourState;

    useEffect(() => {
      if (Array.isArray(persistedTourState)) {
        setPersistedTourState(hasCompletedTour);
      }
    }, [hasCompletedTour, persistedTourState, setPersistedTourState]);

    const currentStop = hasCompletedTour ? undefined : tour[stopIndex];
    const currentStopNumber = currentStop ? stopIndex + 1 : -1;
    const isFirstStop = stopIndex === 0;
    const isLastStop = stopIndex === tour.length - 1;

    const goToNextStop = () => {
      if (isLastStop) {
        setPersistedTourState(true);
        return;
      }
      setStopIndex(stopIndex + 1);
    };

    const goToPreviousStop = () => {
      setStopIndex(Math.max(0, stopIndex - 1));
    };

    const startTour = () => {
      setStopIndex(0);
      setPersistedTourState(false);
    };

    const skipTour = () => {
      setPersistedTourState(true);
    };

    const contextValue = useMemo(
      () => ({
        isFirstStop,
        isLastStop,
        totalStops: tour.length,
        currentStopNumber,
        currentStop,
        hasCompletedTour: isE2E ? true : hasCompletedTour,
        ref,
        goToNextStop,
        goToPreviousStop,
        startTour,
        skipTour,
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        isFirstStop,
        isLastStop,
        currentStopNumber,
        currentStop,
        hasCompletedTour,
        isE2E,
        tour.length,
        // ref is intentionally excluded - it's a stable object
        // goToNextStop, goToPreviousStop, skipTour are stable functions
      ],
    );

    useEffect(() => {
      if (!registerTour || tour.length === 0) {
        return;
      }

      return registerTour({
        id: config.contextName,
        showInGlobalHelp: config.showInGlobalHelp ?? true,
        anchorRef: contextValue.ref,
        hasCompletedTour: contextValue.hasCompletedTour,
        startTour: contextValue.startTour,
      });
    }, [
      config.contextName,
      config.showInGlobalHelp,
      contextValue,
      registerTour,
      tour.length,
    ]);

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
  };

  return {
    Context,
    useTourStop,
    useTour,
    TourProvider,
  };
}
